import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Checkbox,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RecaptchaBadge from '../../components/RecaptchaBadge';
import PasswordFieldWithStrength from '../../components/PasswordFieldWithStrength';
import {
  APP_PATH_LOGIN,
  APP_PATH_TERM_CONDITIONS,
  MAIN_APP_NAME
} from '../../config';
import { useSignUp } from '../../hooks/useSignUp';
import usePageTitle from '../../hooks/usePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    height: '100vh',
    minHeight: '100%'
  },
  grid: {
    height: '100%'
  },
  quoteContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.background.default}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  quote: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: 'url(/images/auth.jpg)',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center'
  },
  quoteInner: {
    textAlign: 'center',
    flexBasis: '600px'
  },
  quoteText: {
    fontWeight: 300
  },
  name: {
    marginTop: theme.spacing(3)
  },
  bio: {},
  contentContainer: {
    height: '100%'
  },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBototm: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  logoImage: {
    marginLeft: theme.spacing(4)
  },
  contentBody: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center'
    }
  },
  form: {
    paddingLeft: 100,
    paddingRight: 100,
    paddingBottom: 125,
    flexBasis: 700,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  title: {
    marginTop: theme.spacing(3),
    color: theme.palette.text.primary
  },
  textField: {
    marginTop: theme.spacing(2)
  },
  policy: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center'
  },
  policyCheckbox: {
    marginLeft: '-14px'
  },
  signUpButton: {
    margin: theme.spacing(2, 0)
  },
  largeLogo: {
    width: theme.spacing(24),
    height: theme.spacing(24)
  }
}));

const SignUp = () => {
  const classes = useStyles();
  const {
    handleSignUp,
    handleChange,
    handleBack,
    hasError,
    formState,
    isPasswordStrongEnough
  } = useSignUp();
  usePageTitle('Sign Up');

  return (
    <div className={classes.root}>
      <Grid className={classes.grid} container>
        <Grid className={classes.quoteContainer} item lg={5}>
          <div className={classes.quote}>
            <Typography
              variant="h1"
              color="textPrimary"
              style={{ fontSize: '3rem', fontWeight: 700 }}>
              {MAIN_APP_NAME}
            </Typography>
          </div>
          <div className={classes.quote}>
            <div className={classes.quoteInner}>
              <Typography className={classes.title} variant="h1">
                Where Talent Meets Opportunity. Work Without Borders.
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid className={classes.content} item lg={7} xs={12}>
          <div className={classes.content}>
            <div className={classes.contentHeader}>
              <IconButton onClick={handleBack}>
                <ArrowBackIcon />
              </IconButton>
            </div>
            <div className={classes.contentBody}>
              <form
                className={classes.form}
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSignUp(formState);
                }}>
                <Typography className={classes.title} variant="h2" gutterBottom>
                  Join Remote Job
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  Find remote work or hire top talent. All in one place
                </Typography>
                <TextField
                  data-test="fullname-register"
                  className={classes.textField}
                  error={hasError('fullName')}
                  fullWidth
                  helperText={
                    hasError('fullName') ? formState.errors.fullName[0] : null
                  }
                  label="Full Name"
                  name="fullName"
                  onChange={handleChange}
                  type="text"
                  value={formState.values.fullName || ''}
                  variant="outlined"
                  autoComplete="name"
                />
                <TextField
                  data-test="email-register"
                  className={classes.textField}
                  error={hasError('email')}
                  fullWidth
                  helperText={
                    hasError('email') ? formState.errors.email[0] : null
                  }
                  label="Email address"
                  name="email"
                  onChange={handleChange}
                  type="text"
                  value={formState.values.email || ''}
                  variant="outlined"
                  autoComplete="username"
                />
                <PasswordFieldWithStrength
                  data-test="password-register"
                  className={classes.textField}
                  error={hasError('password')}
                  fullWidth
                  helperText={
                    hasError('password') ? formState.errors.password[0] : null
                  }
                  label="Password"
                  name="password"
                  onChange={handleChange}
                  value={formState.values.password || ''}
                  variant="outlined"
                />
                <div className={classes.policy}>
                  <Checkbox
                    data-test="check-term-conditions-register"
                    checked={formState.values.policy || false}
                    className={classes.policyCheckbox}
                    color="primary"
                    name="policy"
                    onChange={handleChange}
                  />
                  <Typography
                    className={classes.policyText}
                    color="textSecondary"
                    variant="body1">
                    I have read the{' '}
                    <Link
                      data-test="link-term-conditions-register"
                      color="primary"
                      component={RouterLink}
                      to={APP_PATH_TERM_CONDITIONS}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      variant="h6">
                      Terms and Conditions
                    </Link>
                  </Typography>
                </div>
                {hasError('policy') && (
                  <FormHelperText error>
                    {formState.errors.policy[0]}
                  </FormHelperText>
                )}
                <Button
                  data-test="sing-up-button-register"
                  className={classes.signUpButton}
                  color="primary"
                  disabled={!formState.isValid || !isPasswordStrongEnough()}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained">
                  Get Started
                </Button>
                <Typography color="textSecondary" variant="body1">
                  Already have an account?{' '}
                  <Link
                    data-test="sing-in-link-register"
                    component={RouterLink}
                    to={APP_PATH_LOGIN}
                    variant="h6">
                    Sign in
                  </Link>
                </Typography>
                <RecaptchaBadge />
              </form>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default SignUp;
